import {
  Button,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
} from '@chakra-ui/react';
import { DownloadIcon, SearchIcon, XCircleIcon } from '@heroicons/react/outline';
import axios from 'axios';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { SubscriptionsTabsStatus } from '@blockpulse3/data/shared';
import { SubscriptionsFilterInput } from '@blockpulse3/graphql/hooks';
import { downloadFile } from '@blockpulse3/helpers';
import { useErrorToast } from '@blockpulse3/ui/commons';

import { FundraisingSubscribersTableFilters } from './FundraisingSubscribersTableFilters';
import { TabsStatus } from './utils';

type Props = {
  /* ** List of tab names and other properties ** */
  tabs: TabsStatus[];
  /* ** Controlled active tab index ** */
  activeTabIndex: number;
  /* ** Active filters ** */
  filters: SubscriptionsFilterInput[];
  /* ** Callback on filter update ** */
  onFiltersChange: (value: SubscriptionsFilterInput[]) => void;
  /* ** Callback on search submit ** */
  onSearchInputSubmit: (value: string) => void;
};

/**
 * FundraisingSubscribersControls.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function FundraisingSubscribersControls({
  tabs,
  activeTabIndex,
  filters,
  onFiltersChange,
  onSearchInputSubmit,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nFundraisingSubscribersTable = useTranslations('FundraisingSubscribersTable');

  const errorToast = useErrorToast();

  const { fundraisingId = '' } = useParams();

  const [searchInput, setSearchInput] = useState<string>('');
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  /* ** Filter modal button condition ** */
  const showFilters = [
    SubscriptionsTabsStatus.ALL_STATUS,
    SubscriptionsTabsStatus.PENDING,
  ].includes(tabs[activeTabIndex].name);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchInput(e.target.value);
  };

  const handleSearchInputReset = (): void => {
    setSearchInput('');
    onSearchInputSubmit('');
  };

  const handleSearchInputSubmit = (): void => {
    onSearchInputSubmit(searchInput);
  };

  const handleFiltersChange = (value: SubscriptionsFilterInput[]): void => {
    onFiltersChange(value);
  };

  const handleSubscribersExport = async (): Promise<void> => {
    setIsExportLoading(true);
    try {
      await axios
        .post(
          `${process.env['NX_API_CONTROLLER_ENDPOINT']}/fundraisings/exportSubscriptions`,
          { fundraisingId, filterBy: filters },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            responseType: 'blob',
          },
        )
        .then((response) => {
          downloadFile(response.data, 'export-souscriptions.csv');
        });
    } catch (err) {
      errorToast({ title: i18nFundraisingSubscribersTable('Toast.ExportError') });
    } finally {
      setIsExportLoading(false);
    }
  };

  return (
    <Stack direction={{ base: 'column-reverse', md: 'row' }} justifyContent="space-between">
      <Stack direction={{ base: 'column-reverse', md: 'row' }}>
        <InputGroup width={{ base: 'full', md: '300px' }}>
          <Input
            paddingRight="80px"
            placeholder={i18nGeneral('Search')}
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyDown={(e): void => {
              if (e.key === 'Enter') {
                handleSearchInputSubmit();
              }
            }}
          />
          <InputRightElement
            flexDirection="row-reverse"
            justifyContent="space-between"
            paddingX="1"
            width="80px"
          >
            <IconButton
              aria-label="search-database"
              icon={<Icon as={SearchIcon} />}
              size="sm"
              variant="secondary"
              onClick={handleSearchInputSubmit}
            />
            {!!searchInput && (
              <IconButton
                aria-label="reset"
                icon={<Icon as={XCircleIcon} />}
                size="sm"
                variant="ghost"
                onClick={handleSearchInputReset}
              />
            )}
          </InputRightElement>
        </InputGroup>
        {showFilters && (
          <FundraisingSubscribersTableFilters
            defaultValue={filters}
            onSubmit={handleFiltersChange}
          />
        )}
      </Stack>
      <Button
        isDisabled={!tabs[activeTabIndex].count}
        isLoading={isExportLoading}
        loadingText={i18nFundraisingSubscribersTable('LoadingExport')}
        rightIcon={<Icon as={DownloadIcon} />}
        variant="secondary"
        onClick={handleSubscribersExport}
      >
        {i18nGeneral('Export')}
      </Button>
    </Stack>
  );
}

export type FundraisingSubscribersControlsProps = Props;
